import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { faAddressBook, faBringForward, faComment, faEnvelope, faPlusSquare, faTools } from '@fortawesome/pro-duotone-svg-icons';
import { faComment as faCommentSolid, faEnvelope as faEnvelopeSolid } from '@fortawesome/pro-solid-svg-icons';
import { Select, Store } from '@ngxs/store';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';
import _ from 'lodash';
import { Observable, Subject, of, timer } from 'rxjs';
import { debounce, delay, distinctUntilChanged, filter, takeUntil, tap } from 'rxjs/operators';
import { toggleDisable } from 'src/app/components/utility/animation/animations';
import { WellKnownIconsEnum } from 'src/app/helper/iconHelper';
import { FeatureEnum } from 'src/app/samwebDescription/samwin/FeatureEnum';
import { FaviconService } from 'src/app/services/app/favicon.service';
import { CloseAddBrickDialogAction, ToggleAddBrickDialog } from 'src/app/store/dashboard/brick-menu/add-new-brick-dialog.actions';
import { AddNewBrickDialogState } from 'src/app/store/dashboard/brick-menu/add-new-brick-dialog.state';
import { DirectoryEditingBehaviourState } from 'src/app/store/dashboard/brick-wall/brick-behaviour-state/brickTypes/directory-behaviour/directory-editing/directory-editing-behaviour.state';
import { StartBrickEditing, StopBrickEditing } from 'src/app/store/dashboard/brick-wall/brick-instances/brick-instances.actions';
import { BrickInstancesSelectors } from 'src/app/store/dashboard/brick-wall/brick-instances/brick-instances.selectors';
import { ToggleChatListAction } from 'src/app/store/dashboard/brick-wall/chat-list/chat-list.actions';
import { ChatListSelectors } from 'src/app/store/dashboard/brick-wall/chat-list/chat-list.selectors';
import { CloseDirectoryEditingList, OpenDirectoryEditingList } from 'src/app/store/dashboard/directory-editing-list/directory-editing-list.actions';
import { DirectoryEditingListState } from 'src/app/store/dashboard/directory-editing-list/directory-editing-list.state';
import { CloseEmailListDialogAction, OpenEmailListDialogAction } from 'src/app/store/dashboard/email-list/email-list-dialog.actions';
import { EmailListDialogState } from 'src/app/store/dashboard/email-list/email-list-dialog.state';
import { ChatsSelectors } from 'src/app/store/samwin/chats/chats.selectors';
import { ChatId } from 'src/app/store/samwin/chats/models/chat-id';
import { EmailBehaviourState } from 'src/app/store/samwin/email/email-behaviour/email-behaviour.state';
import { CommunicationFailureSelectors } from 'src/app/store/server-communication/communication-failure.selectors';
import { EnabledFeaturesState } from 'src/app/store/session/enabled-features/enabled-features.state';
import { CloseSessionControlDialog, ToggleSessionControlDialog } from 'src/app/store/session/session.actions';
import { SessionSelectors } from 'src/app/store/session/session.selectors';
import { SessionState } from 'src/app/store/session/session.state';
import { ToggleGlobalSettingsDialog } from 'src/app/store/settings/settings.actions';
import { SettingsState } from 'src/app/store/settings/settings.state';
import { SamwinVersion } from 'src/app/store/utility/samwin-version/samwin-version';
import { DirectoryEntryRemoteDto } from 'src/generated/swagger';

@Component({
   selector: 'samweb-home-bar',
   templateUrl: './home-bar.component.html',
   styleUrls: ['./home-bar.component.scss'],
   changeDetection: ChangeDetectionStrategy.OnPush,
   animations: [toggleDisable],
})
export class HomeBarComponent implements AfterViewInit, OnDestroy {
   @Select(EnabledFeaturesState.isFeatureEnabled(FeatureEnum.WEB_AGENT_CHANGE_BRICKS_AND_WALL_SETTINGS))
   public isFeatureEnabled$: Observable<boolean>;

   @Select(BrickInstancesSelectors.isBrickWallEditable)
   public isBrickWallEditingEnabled$: Observable<boolean>;

   @Select(BrickInstancesSelectors.isSavingBrickWall)
   public isSavingBrickWall$: Observable<boolean>;

   @Select(AddNewBrickDialogState.isBrickMenuOpen)
   public isBrickMenuOpen$: Observable<boolean>;

   @Select(SettingsState.isSettingsDialogOpen)
   public isGlobalSettingsDialogOpen$: Observable<boolean>;

   @Select(CommunicationFailureSelectors.isCommunicationHealthy)
   public isCommunicationHealthy$: Observable<boolean>;

   @Select(SessionState.getIsSignOutDialogOpen)
   public isSignOutDialogOpen$: Observable<boolean>;

   public isEmailListOpen$: Observable<boolean>;

   @Select(EmailBehaviourState.getOpenEmailWindows)
   public openEmailWindows$: Observable<string[]>;

   @Select(DirectoryEditingBehaviourState.getEntriesInEditing)
   public directoryEntriesInEditing$: Observable<DirectoryEntryRemoteDto[]>;

   public isDirectoryEditingListOpen$: Observable<boolean>;

   @Select(ChatListSelectors.isChatListOpen)
   isChatListOpen$: Observable<boolean>;

   @Select(ChatsSelectors.getOpenChats)
   public openChats$: Observable<ChatId[]>;

   @Select(ChatsSelectors.getTotalNumberOfUnreadMessages)
   public numberOfUnreadChatMessages$: Observable<number>;
   private lastNumberOfUnreadMessagesPerChat: Map<ChatId, number | null>;

   @Select(SessionSelectors.isLogoutDisabled)
   public isLogoutDisabled$: Observable<boolean>;

   private destroy$ = new Subject();

   public directoryEditIcon: IconDefinition = faAddressBook;

   public emailIcon: IconDefinition = faEnvelope;
   public emailSolidIcon: IconDefinition = faEnvelopeSolid;
   public emailAdditionalClass = 'multiple-envelope';

   public chatIcon: IconDefinition = faComment;
   public chatSolidIcon: IconDefinition = faCommentSolid;

   public applyEditIcon: IconDefinition = WellKnownIconsEnum.CONFIRM.icon;
   public cancelEditIcon: IconDefinition = WellKnownIconsEnum.CANCEL.icon;
   public toggleBrickMenuIcon: IconDefinition = faPlusSquare;
   public settingsIcon: IconDefinition = faTools;
   public toggleBrickLayoutIcon: IconDefinition = faBringForward;
   public signOutIcon: IconDefinition = WellKnownIconsEnum.SIGN_OUT.icon;

   public clientVersion = SamwinVersion.VERSION;

   @ViewChild('addBricksMenuPopupControl', { read: ElementRef })
   public brickMenuTooltipControl: ElementRef;

   @ViewChild('globalSettingsPopupControl', { read: ElementRef })
   public globalSettingsPopupControl: ElementRef;

   @ViewChild('signOutDialogPopupControl', { read: ElementRef })
   public signOutDialogPopupControl: ElementRef;

   @ViewChild('addBricksMenuTooltipDirective')
   public addBricksMenuTooltipDirective: TooltipDirective;

   @ViewChild('globalSettingsTooltipDirective')
   public globalSettingsTooltipDirective: TooltipDirective;

   @ViewChild('signOutDialogTooltipDirective')
   public signOutDialogTooltipDirective: TooltipDirective;

   @ViewChild('emailListPopupControl', { read: ElementRef })
   public emailListPopupControl: ElementRef;

   @ViewChild('emailListTooltipDirective')
   public emailListTooltipDirective: TooltipDirective;

   @ViewChild('directoryEditingListPopupControl', { read: ElementRef })
   public directoryEditingListPopupControl: ElementRef;

   @ViewChild('directoryEditingListTooltipDirective')
   public directoryEditingListTooltipDirective: TooltipDirective;

   @ViewChild('chatListPopupControl', { read: ElementRef })
   public chatListPopupControl: ElementRef;

   @ViewChild('chatListTooltipDirective')
   public chatListTooltipDirective: TooltipDirective;

   @ViewChild('unreadChatMessagesBadge', { read: ElementRef })
   public unreadChatMessagesBadge: ElementRef;

   constructor(private store: Store, private faviconService: FaviconService) {}

   ngAfterViewInit(): void {
      // Note: adds debounce to closing the dialog windows, this is to bridge the traveltime between button and list.
      this.isEmailListOpen$ = this.store.select(EmailListDialogState.isEmailListOpen).pipe(debounce((ev) => (!ev ? timer(10) : of({}))));
      this.isDirectoryEditingListOpen$ = this.store.select(DirectoryEditingListState.isDirectoryEditingListOpen).pipe(
         distinctUntilChanged(),
         debounce((ev) => (!ev ? timer(10) : of({})))
      );

      this.isBrickMenuOpen$
         .pipe(
            tap((isBrickMenuOpen: boolean) => this.handleTooltipPopup(isBrickMenuOpen, this.addBricksMenuTooltipDirective, this.brickMenuTooltipControl)),
            takeUntil(this.destroy$)
         )
         .subscribe();

      this.isGlobalSettingsDialogOpen$
         .pipe(
            delay(350),
            tap((isGlobalSettingsDialogOpen: boolean) =>
               this.handleTooltipPopup(isGlobalSettingsDialogOpen, this.globalSettingsTooltipDirective, this.globalSettingsPopupControl)
            ),
            takeUntil(this.destroy$)
         )
         .subscribe();

      this.isSignOutDialogOpen$
         .pipe(
            tap((isSignOutDialogOpen: boolean) =>
               this.handleTooltipPopup(isSignOutDialogOpen, this.signOutDialogTooltipDirective, this.signOutDialogPopupControl)
            ),
            takeUntil(this.destroy$)
         )
         .subscribe();

      this.isEmailListOpen$
         .pipe(
            tap((isEmailListOpen: boolean) => this.handleTooltipPopup(isEmailListOpen, this.emailListTooltipDirective, this.emailListPopupControl)),
            takeUntil(this.destroy$)
         )
         .subscribe();

      this.isDirectoryEditingListOpen$
         .pipe(
            tap((isDirectoryEditingListOpen: boolean) =>
               this.handleTooltipPopup(isDirectoryEditingListOpen, this.directoryEditingListTooltipDirective, this.directoryEditingListPopupControl)
            ),
            takeUntil(this.destroy$)
         )
         .subscribe();

      this.isChatListOpen$
         .pipe(
            tap((isChatListOpen: boolean) => this.handleTooltipPopup(isChatListOpen, this.chatListTooltipDirective, this.chatListPopupControl)),
            takeUntil(this.destroy$)
         )
         .subscribe();

      this.store
         .select(ChatsSelectors.getNumberOfUnreadMessagesForAllChats)
         .pipe(
            tap((newNumberOfUnreadMessagesPerChat) => {
               if (this.anyNewChatMessages(newNumberOfUnreadMessagesPerChat)) {
                  this.restartUnreadChatMessagesBadgeAnimation();
                  this.faviconService.letFaviconBlink(400);
               }

               this.lastNumberOfUnreadMessagesPerChat = newNumberOfUnreadMessagesPerChat;
            }),
            takeUntil(this.destroy$)
         )
         .subscribe();

      this.isDirectoryEditingListOpen$
         .pipe(
            tap((isDirectoryEditingListOpen: boolean) => {
               if (_.isNil(this.directoryEditingListTooltipDirective)) {
                  return;
               }
               if (!isDirectoryEditingListOpen) {
                  this.directoryEditingListTooltipDirective.hide();
                  return;
               }
               if (!_.isNil(this.directoryEditingListPopupControl)) {
                  this.directoryEditingListTooltipDirective.toggle(this.directoryEditingListPopupControl.nativeElement, isDirectoryEditingListOpen);
                  return;
               }
            }),
            takeUntil(this.destroy$)
         )
         .subscribe();

      this.isLogoutDisabled$
         .pipe(
            filter((isLogoutDisabled) => isLogoutDisabled),
            takeUntil(this.destroy$)
         )
         .subscribe(() => this.closeSignOutDialog());
   }

   toggleSignOutDialog() {
      this.store.dispatch(new ToggleSessionControlDialog());
   }

   closeSignOutDialog() {
      this.store.dispatch(new CloseSessionControlDialog());
   }

   startBrickEditing() {
      this.store.dispatch(new StartBrickEditing());
   }

   stopBrickEditing(saveChanges: boolean) {
      this.store.dispatch(new StopBrickEditing(saveChanges));
   }

   toggleBrickMenu() {
      this.store.dispatch(new ToggleAddBrickDialog());
   }

   toggleGlobalSettings() {
      this.store.dispatch(new ToggleGlobalSettingsDialog());
   }

   openEmailListDialog() {
      this.store.dispatch(new OpenEmailListDialogAction());
   }

   closeEmailListDialog() {
      this.store.dispatch(new CloseEmailListDialogAction());
   }

   openDirectoryEditingList() {
      this.store.dispatch(new OpenDirectoryEditingList());
   }

   closeDirectoryEditingList() {
      this.store.dispatch(new CloseDirectoryEditingList());
   }

   toggleChatList() {
      this.store.dispatch(new ToggleChatListAction());
   }

   closeBrickMenu() {
      this.store.dispatch(new CloseAddBrickDialogAction());
   }

   ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
   }

   private handleTooltipPopup(isOpen: boolean, tooltipDirective: TooltipDirective, popupControl: ElementRef<any>): void {
      if (_.isNil(tooltipDirective)) {
         return;
      }

      if (!isOpen) {
         tooltipDirective.hide();
         return;
      }

      if (!_.isNil(popupControl)) {
         tooltipDirective.toggle(popupControl, isOpen);
         return;
      }
   }

   private anyNewChatMessages(newNumberOfUnreadMessagesPerChat: Map<ChatId, number | null>): boolean {
      let anyNewMessages = false;

      newNumberOfUnreadMessagesPerChat.forEach((unread, id) => {
         if (_.isNil(unread) || unread === 0) return;

         const lastUnreadForChat = this.lastNumberOfUnreadMessagesPerChat.has(id) ? this.lastNumberOfUnreadMessagesPerChat.get(id) : 0;
         if (_.isNil(lastUnreadForChat) || unread > lastUnreadForChat) anyNewMessages = true;
      });

      return anyNewMessages;
   }

   private restartUnreadChatMessagesBadgeAnimation(): void {
      if (!this.unreadChatMessagesBadge) return;

      this.unreadChatMessagesBadge.nativeElement.style.animation = 'none';
      setTimeout(() => (this.unreadChatMessagesBadge.nativeElement.style.animation = ''), 0);
   }
}
